import { ApiStreamError } from './errors';
import { isNdjsonContentType, NDJSON_CONTENT_TYPE, readNdjson } from './ndjson';

// Browsers refuse to set these, and humarestclient's default headers null out `User-Agent`.
const UNSETTABLE_HEADERS = new Set(['user-agent']);

const setHeaders = (target, source = {}) => {
  Object.entries(source || {}).forEach(([name, value]) => {
    if (value === undefined || value === null || UNSETTABLE_HEADERS.has(name.toLowerCase())) return;
    target.set(name, String(value));
  });
  return target;
};

const buildQueryString = (apiClient, queryParams = {}) => {
  const params = new URLSearchParams();
  Object.entries(queryParams || {}).forEach(([name, value]) => {
    if (value === undefined || value === null) return;
    (Array.isArray(value) ? value : [value]).forEach((item) =>
      params.append(name, apiClient.paramToString(item))
    );
  });
  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
};

const defaultJsonFallback = (data) => {
  if (data === undefined || data === null) return [];
  return Array.isArray(data) ? data : [data];
};

/**
 * Performs an authenticated request through a humarestclient `ApiClient` and streams the NDJSON
 * response as batches of parsed items.
 *
 * The URL, encoding of path params and auth headers come from the same `ApiClient` the generated SDK
 * uses, so token refresh (`setAccessToken`) and the dev API proxy keep working.
 *
 * If the server answers with plain `application/json` instead of NDJSON, the payload is converted
 * with `jsonFallback` and yielded as a single batch, so callers can migrate endpoints independently.
 *
 * @param {Object} config
 * @param {Object} config.apiClient humarestclient ApiClient, e.g. `state.apis.userEventsApi.apiClient`.
 * @param {string} config.path Endpoint path with `{param}` placeholders.
 * @param {string} [config.method='GET']
 * @param {Object} [config.pathParams]
 * @param {Object} [config.queryParams] Array values are sent as repeated query keys.
 * @param {*} [config.body] Serialized as JSON when provided.
 * @param {Object} [config.headers] Extra headers; override the client's defaults.
 * @param {AbortSignal} [config.signal]
 * @param {number} [config.batchMs] See `readNdjson`.
 * @param {number} [config.maxBatchSize] See `readNdjson`.
 * @param {(data: *) => Array} [config.jsonFallback] Maps a non-NDJSON JSON body to an array of items.
 * @returns {AsyncGenerator<Array<*>>}
 */
export async function* streamNdjsonRequest({
  apiClient,
  path,
  method = 'GET',
  pathParams = {},
  queryParams,
  body,
  headers,
  signal,
  batchMs,
  maxBatchSize,
  jsonFallback = defaultJsonFallback,
}) {
  if (typeof apiClient?.buildUrl !== 'function') {
    throw new TypeError('streamNdjsonRequest requires a humarestclient ApiClient instance');
  }

  const hasBody = body !== undefined && body !== null;
  const requestHeaders = setHeaders(new Headers(), apiClient.defaultHeaders);
  requestHeaders.set('Accept', `${NDJSON_CONTENT_TYPE}, application/json;q=0.9`);
  if (hasBody) requestHeaders.set('Content-Type', 'application/json');
  setHeaders(requestHeaders, headers);

  const response = await fetch(
    apiClient.buildUrl(path, pathParams) + buildQueryString(apiClient, queryParams),
    {
      method,
      signal,
      headers: requestHeaders,
      body: hasBody ? JSON.stringify(body) : undefined,
    }
  );

  if (!response.ok) {
    throw await ApiStreamError.fromResponse(response);
  }

  const contentType = response.headers.get('content-type') || '';
  if (!isNdjsonContentType(contentType) && contentType.includes('json')) {
    const items = jsonFallback(await response.json());
    if (items.length) yield items;
    return;
  }

  yield* readNdjson(response, { batchMs, maxBatchSize });
}
